import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import QuestionCard from './QuestionCard';
import ProgressBar from './ProgressBar';
import { questions } from '../data/questions';
import styles from './Assessment.module.css';

const isOthersOption = (option) => typeof option === 'string' && option.trim().toLowerCase() === 'others';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function Assessment({ onFinish }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [otherTexts, setOtherTexts] = useState({});

  // Shown as a final step once all questions are answered, so we capture
  // a fresh email directly on the assessment instead of relying on
  // whatever localStorage happened to save from the earlier lead form.
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phone, setPhone] = useState('');

  const total = questions.length;
  const question = questions[currentIndex];
  const answer = answers[question.id];
  const otherText = otherTexts[question.id];

  const othersIndex = question.options.findIndex(isOthersOption);
  const isOthersSelected =
    othersIndex !== -1 &&
    (question.type === 'multi' ? Array.isArray(answer) && answer.includes(othersIndex) : answer === othersIndex);

  const hasAnswer =
    (question.type === 'multi' ? Array.isArray(answer) && answer.length > 0 : answer !== undefined && answer !== null) &&
    (!isOthersSelected || (otherText && otherText.trim().length > 0));

  const handleAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
  };

  const handleOtherTextChange = (value) => {
    setOtherTexts((prev) => ({ ...prev, [question.id]: value }));
  };

  const goNext = () => {
    if (!hasAnswer) return;
    if (currentIndex === total - 1) {
      setShowEmailGate(true);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const goPrev = () => {
    if (showEmailGate) {
      setShowEmailGate(false);
      return;
    }
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError('Enter a valid work email to see your results.');
      return;
    }
    setEmailError('');
onFinish(
    answers,
    otherTexts,
    email.trim(),
    phone.trim()
);
  };

  if (showEmailGate) {
    return (
      <motion.div
        className={styles.airaRoot}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className={styles.header}>
          <span className={styles.counter}>Almost there</span>
          <ProgressBar current={total} total={total} />
        </div>

        <motion.div
          className={`${styles.card} aira-card`}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <h2 className={styles.question}>Where should we send your results?</h2>
          <p className={styles.hint}>Enter your work email to unlock your AI readiness score.</p>

          <form onSubmit={handleEmailSubmit}>
            <input
              type="email"
              className={styles.otherInput}
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            {emailError && <p className={styles.hint}>{emailError}</p>}
            <input
    type="tel"
    placeholder="+91 9876543210"
    value={phone}
    onChange={(e) => setPhone(e.target.value)}
    required
/>

            <div className={styles.nav}>
              <button type="button" className={styles.secondaryButton} onClick={goPrev}>
                Previous
              </button>
              <button type="submit" className="aira-button">
                See My Results
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles.airaRoot}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className={styles.header}>
        <span className={styles.counter}>
          Question {currentIndex + 1} of {total}
        </span>
        <ProgressBar current={currentIndex + 1} total={total} />
      </div>

      <AnimatePresence mode="wait">
        <QuestionCard
          key={question.id}
          question={question}
          answer={answer}
          onAnswer={handleAnswer}
          otherText={otherText}
          onOtherTextChange={handleOtherTextChange}
        />
      </AnimatePresence>

      <div className={styles.nav}>
        <button type="button" className={styles.secondaryButton} onClick={goPrev} disabled={currentIndex === 0}>
          Previous
        </button>
        <button type="button" className="aira-button" onClick={goNext} disabled={!hasAnswer}>
          {currentIndex === total - 1 ? 'Finish Assessment' : 'Next'}
        </button>
      </div>
    </motion.div>
  );
}